import { IconName } from '@subframe/core';
import { TimelineStepStatus, TimelineStep as TimelineStepType } from 'modules/Recruit/types';
import React from 'react';
import { Button } from 'subframe/components/Button';
import { IconWithBackground } from 'subframe/components/IconWithBackground';
import { TagScoreTxt } from 'subframe/components/TagScoreTxt';

import { formatLocalDate } from 'utils/helper';
import { AttachmentCard } from './AttachmentCard';

import { Loader } from 'subframe';

type TagScoreTxtVariant = 'default' | 'alert' | 'medium' | 'success' | 'plain';
interface TimelineStepProps {
  step: TimelineStepType;
  isLastStep: boolean;
  onRetrigger?: (stepName: string) => void;
  isRetriggering?: boolean;
}

export const TimelineStep: React.FC<TimelineStepProps> = ({
  step,
  isLastStep,
  onRetrigger,
  isRetriggering = false,
}) => {
  const { stage_name, status, started_at, completed_at, can_resend, document } = step;

  const getStatusConfig = (status: TimelineStepStatus) => {
    switch (status) {
      case 'COMPLETED':
        return {
          bgColor: 'bg-success-600',
          textColor: 'text-success-700',
          icon: 'FeatherCheckCircle' as IconName,
          variant: 'success' as const,
          displayText: 'Completed',
          tagVariant: 'success' as TagScoreTxtVariant,
        };
      case 'IN_PROGRESS':
        return {
          bgColor: 'bg-warning-600',
          textColor: 'text-warning-700',
          icon: 'FeatherClock' as IconName,
          variant: 'warning' as const,
          displayText: 'In Progress',
          tagVariant: 'medium' as TagScoreTxtVariant,
        };
      case 'PENDING':
        return {
          bgColor: 'bg-neutral-border',
          textColor: 'text-subtext-color',
          icon: 'FeatherClock' as IconName,
          variant: 'neutral' as const,
          displayText: 'Waiting for previous step',
        };
      case 'ATTEMPTED':
        return {
          bgColor: 'bg-warning-600',
          textColor: 'text-warning-700',
          icon: 'FeatherAlertTriangle' as IconName,
          variant: 'warning' as const,
          displayText: 'Attempted',
          tagVariant: 'normal' as TagScoreTxtVariant,
        };
      case 'EXPIRED':
        return {
          bgColor: 'bg-error-600',
          textColor: 'text-error-700',
          icon: 'FeatherAlertTriangle' as IconName,
          variant: 'error' as const,
          displayText: 'Expired',
          tagVariant: 'alert' as TagScoreTxtVariant,
        };
      case 'REJECTED':
        return {
          bgColor: 'bg-error-600',
          textColor: 'text-error-700',
          icon: 'FeatherXCircle' as IconName,
          variant: 'error' as const,
          displayText: 'Rejected',
          tagVariant: 'alert' as TagScoreTxtVariant,
        };
      default:
        return {
          bgColor: 'bg-neutral-border',
          textColor: 'text-subtext-color',
          icon: 'FeatherCircle' as IconName,
          variant: 'neutral' as const,
          displayText: 'Waiting for previous step',
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <div className="flex w-full items-center gap-4">
      {/* Timeline Connector and Icon */}
      <div className="flex flex-col items-center self-stretch">
        <div className={`flex h-6 w-0.5 flex-none flex-col ${config.bgColor}`} />
        <IconWithBackground variant={config.variant} size="small" icon={config.icon} />
        {!isLastStep && (
          <div className={`flex w-0.5 grow shrink-0 basis-0 flex-col ${config.bgColor}`} />
        )}
      </div>

      <div className="flex items-center gap-4 py-3">
        <div className="flex w-48 flex-none flex-col items-start gap-1">
          {started_at && (
            <span className="text-label font-label text-subtext-color">
              Sent: {formatLocalDate(started_at, true)}
            </span>
          )}
          {completed_at && status === 'COMPLETED' && (
            <span className={`text-label font-label ${config.textColor}`}>
              Completed: {formatLocalDate(completed_at, true)}
            </span>
          )}
          {!completed_at && (
            <span className={`text-label font-label ${config.textColor}`}>
              {config.displayText}
            </span>
          )}
        </div>

        {/* Step Card */}
        <div className="flex h-16 w-96 flex-none items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-6 py-4 shadow-sm">
          <span className="text-body-bold font-body-bold text-default-font">{stage_name}</span>
          <div className="flex items-center gap-2">
            {can_resend
              ? onRetrigger && (
                  <Button
                    variant="brand-tertiary"
                    icon="FeatherSend"
                    onClick={() => onRetrigger(stage_name)}
                    disabled={isRetriggering}
                  >
                    {isRetriggering ? <Loader size="small" /> : 'Resend Link'}
                  </Button>
                )
              : config.tagVariant && (
                  <TagScoreTxt score={config.displayText} variant={config.tagVariant} />
                )}
          </div>
        </div>

        {document && <AttachmentCard attachment={document} />}
      </div>
    </div>
  );
};
